import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const NaverCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // 이미 처리했으면 스킵 (React Strict Mode 중복 실행 방지)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    handleNaverCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNaverCallback = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      const savedState = localStorage.getItem('naver_state');

      // WebView 환경 감지
      const isWebView = navigator.userAgent.toLowerCase().includes('wv') ||
                        navigator.userAgent.toLowerCase().includes('webview');

      console.log('[NaverCallback] URL state:', state);
      console.log('[NaverCallback] Saved state:', savedState);
      console.log('[NaverCallback] isWebView:', isWebView);

      // state 검증 (WebView에서는 localStorage가 유지 안 되므로 스킵)
      if (!isWebView && (!state || state !== savedState)) {
        console.error('State 불일치 - URL:', state, 'Saved:', savedState);
        alert('인증에 실패했습니다.');
        navigate('/login', { replace: true });
        return;
      }

      if (!code) {
        console.error('Authorization code 없음');
        alert('인증에 실패했습니다.');
        navigate('/login', { replace: true });
        return;
      }

      console.log('[NaverCallback] 네이버 인증 코드 받음:', code);

      // 동적 BASE_URL 설정
      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      const redirectUri = `${baseUrl}/auth/callback/naver`;
      const clientId = process.env.REACT_APP_NAVER_CLIENT_ID;
      const clientSecret = process.env.REACT_APP_NAVER_CLIENT_SECRET;

      console.log('[NaverCallback] Redirect URI:', redirectUri);

      // 토큰 교환 (Vercel Serverless Function 사용)
      let tokenData = null;
      try {
        console.log('[NaverCallback] 토큰 요청 중...');
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        const tokenResponse = await fetch('/api/naver/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            state,
            client_id: clientId,
            client_secret: clientSecret,
            redirect_uri: redirectUri,
          }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        tokenData = await tokenResponse.json();
        console.log('[NaverCallback] 토큰 응답:', tokenData);
      } catch (proxyError) {
        console.error('[NaverCallback] 토큰 요청 실패:', proxyError);
      }

      if (!tokenData || tokenData.error) {
        console.error('토큰 교환 실패:', tokenData);
        alert('로그인에 실패했습니다. 잠시 후 다시 시도해주세요.');
        navigate('/login', { replace: true });
        return;
      }

      // 사용자 정보 가져오기 (Vercel Serverless Function 사용)
      let userData = null;
      try {
        console.log('[NaverCallback] 사용자 정보 요청 중...');
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        const userResponse = await fetch('/api/naver/userinfo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            access_token: tokenData.access_token,
          }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        userData = await userResponse.json();
        console.log('[NaverCallback] 사용자 정보:', userData);
      } catch (proxyError) {
        console.error('[NaverCallback] 사용자 정보 요청 실패:', proxyError);
      }

      if (!userData || userData.resultcode !== '00') {
        console.error('사용자 정보 가져오기 실패:', userData);
        alert('사용자 정보를 가져오는데 실패했습니다.');
        navigate('/login', { replace: true });
        return;
      }

      const naverUser = userData.response;
      const snsId = `naver_${naverUser.id}`;
      const email = naverUser.email || '';
      const name = naverUser.name || naverUser.nickname || '';

      console.log('[NaverCallback] 네이버 사용자:', naverUser);
      console.log('[NaverCallback] SNS ID:', snsId);

      // 1. 먼저 Supabase Auth 세션 생성
      console.log('[NaverCallback] Supabase Auth 세션 생성 중...');
      await login(snsId);
      console.log('[NaverCallback] Supabase Auth 세션 생성 완료');

      // 2. 이제 인증된 상태에서 DB 조회 (대소문자 구분 없이)
      console.log('[NaverCallback] DB 조회 시작...');
      const { data: existingCustomer, error } = await supabase
        .from('customers')
        .select('*')
        .ilike('sns_id', snsId)
        .maybeSingle();

      console.log('[NaverCallback] DB 조회 완료:', existingCustomer ? '회원 존재' : '회원 없음', error);

      if (existingCustomer) {
        // 기존 회원 - 메인으로 이동 (replace로 callback 히스토리 제거)
        console.log('[NaverCallback] 기존 회원:', existingCustomer.nickname);
        navigate('/main', { replace: true });
      } else {
        // 신규 회원 - 회원가입 플로우로 이동 (replace로 callback 히스토리 제거)
        console.log('[NaverCallback] 신규 회원 - 회원가입 진행');
        navigate('/signup/phone-verification', {
          replace: true,
          state: {
            snsType: 'naver',
            snsId,
            email,
            name,
          },
        });
      }

      // state 정리
      localStorage.removeItem('naver_state');
    } catch (error) {
      console.error('네이버 로그인 처리 오류:', error);
      alert('로그인 중 오류가 발생했습니다.');
      navigate('/login', { replace: true });
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#1e1e1e',
        color: '#ffffff',
        fontFamily: 'SUIT, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <h2>네이버 로그인 처리 중...</h2>
        <p>잠시만 기다려주세요.</p>
      </div>
    </div>
  );
};

export default NaverCallback;
